// libraries.
import { useState } from "react";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";

// styles.
import { StyledRulesTable } from "../styles";

// components.
import { EditStepModal } from "./EditStepModal";
import { ConfirmToggleModal } from "./ConfirmToggleModal";
import { CreateRuleModal } from "./CreateRuleModal";

// interfaces.
import { IncentiveStep } from "../rulesData";
import { RawRuleItem } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

const CATEGORY_LABELS: Record<IncentiveStep["category"], string> = {
  transicion: "Transición",
  bono: "Bono",
  descuento: "Descuento",
  remate: "Remate",
};

const formatDayRange = (dayRange: [number, number | null]): string => {
  const [from, to] = dayRange;
  if (to === null) return `Día ${from}+`;
  return `Día ${from} – ${to}`;
};

export const RulesTableTab = () => {
  const { country, email } = useUserProfile();
  const resolvedCountry = country || "CO";
  const { incentiveStepsCO, incentiveStepsMX, getRawRule, toggleRule, isSaving } = useRulesSlice();
  const steps = resolvedCountry === "MX" ? incentiveStepsMX : incentiveStepsCO;
  const countryLabel = resolvedCountry === "MX" ? "México" : "Colombia";

  const [editingStep, setEditingStep] = useState<IncentiveStep | null>(null);
  const [editingRawRule, setEditingRawRule] = useState<RawRuleItem | null>(null);
  const [confirmToggleRule, setConfirmToggleRule] = useState<RawRuleItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const handleEdit = (step: IncentiveStep) => {
    setEditingStep(step);
    setEditingRawRule(getRawRule(step.id, resolvedCountry) ?? null);
  };

  const handleClose = () => {
    setEditingStep(null);
    setEditingRawRule(null);
  };

  const handleToggleConfirm = () => {
    if (confirmToggleRule) toggleRule(confirmToggleRule, resolvedCountry, email);
    setConfirmToggleRule(null);
  };

  return (
    <>
      <StyledRulesTable>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Reglas de incentivos para <strong>{countryLabel}</strong>. Ordenadas por prioridad de ejecución.
          </p>
          <button className="action-btn btn-edit" onClick={() => setShowCreate(true)}>
            ＋ Nueva regla
          </button>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th style={{ width: "30%", textAlign: "left" }}>Nombre</th>
              <th style={{ width: "10%" }}>Categoría</th>
              <th style={{ width: "12%" }}>Rango de días</th>
              <th style={{ width: "8%" }}>Ajuste</th>
              <th>Prioridad</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, idx) => {
              const raw = getRawRule(step.id, resolvedCountry);
              const isActive = raw?.is_active === "active";

              return (
                <tr key={step.id}>
                  <td>
                    <div className="cell-centered" style={{ color: "#999" }}>{idx + 1}</div>
                  </td>

                  <td>
                    <div className="cell-name">{step.name}</div>
                    <div className="cell-description" style={{ fontSize: 12, marginTop: 2 }}>
                      {step.description}
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered">
                      <span className={`badge-type ${step.category}`}>
                        {CATEGORY_LABELS[step.category]}
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered" style={{ whiteSpace: "nowrap" }}>
                      {formatDayRange(step.dayRange)}
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered" style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                      {step.adjustment || "—"}
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered">
                      {raw?.rule.priority ?? "—"}
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered">
                      <span className={`badge-status ${isActive ? "active" : "inactive"}`}>
                        {isActive ? "● Activa" : "○ Inactiva"}
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="cell-centered gap-2">
                      <button
                        className="action-btn btn-edit"
                        onClick={() => handleEdit(step)}
                        title="Editar regla"
                        disabled={isSaving}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        className={`action-btn ${isActive ? "btn-deactivate" : "btn-activate"}`}
                        onClick={() => raw && setConfirmToggleRule(raw)}
                        disabled={isSaving || !raw}
                        title={isActive ? "Desactivar regla" : "Activar regla"}
                      >
                        {isActive ? "⏸ Desactivar" : "▶ Activar"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {steps.length === 0 && (
          <p className="text-muted text-center mt-4">No hay reglas cargadas.</p>
        )}
      </StyledRulesTable>

      <EditStepModal
        step={editingStep}
        rawRule={editingRawRule}
        country={resolvedCountry}
        onClose={handleClose}
      />

      <ConfirmToggleModal
        rawRule={confirmToggleRule}
        onConfirm={handleToggleConfirm}
        onClose={() => setConfirmToggleRule(null)}
      />

      <CreateRuleModal
        show={showCreate}
        country={resolvedCountry}
        onClose={() => setShowCreate(false)}
      />
    </>
  );
};
